import { Box3, Box3Helper, Matrix4, Object3D } from 'three';
import { OBB } from 'three/examples/jsm/math/OBB.js';

import { MainView } from '../../visualisation/main-view';
import { Load } from '../../../core/types';

import * as YUKA from 'yuka/build/yuka.module.js';

export class CollisionDetection {
  private mainView: MainView;
  private boundingBoxCollection: Box3[];

  constructor(mainView: MainView) {
    this.mainView = mainView;
  }

  private prepareBoundingBoxes(checkedObject: Object3D | Load, objectId: number = null): void {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const checkedObjectYPosition: number = checkedObject.name.includes('container')
      ? (checkedObject as Object3D).position.y
        ? (checkedObject as Object3D).position.y
        : (checkedObject as Object3D).children[0].position.y
      : (checkedObject as Object3D).position.y;
    const checkedObjectHeight: number = checkedObject.name.includes('container')
      ? ((checkedObject as Object3D).children[0].children[0] as unknown as Load).height
      : (checkedObject as Load).height;
    const isAllowedOutOfSpace: boolean =
      (checkedObject as Load)?.allowOutOfSpace ||
      ((checkedObject as Object3D).children[0].children[0] as unknown as Load)?.allowOutOfSpace;
    const loadsIds: number[] = this.mainView.loads
      .map((load: Object3D | Load) => {
        return load.name.includes('pivot') ? (load as Object3D).children[0] : load;
      })
      .filter((load: Object3D | Load) => {
        return (
          ((load as Object3D).position.y < checkedObjectYPosition &&
            (load as Load).height > checkedObjectHeight) ||
          (load as Object3D).position.y >= checkedObjectYPosition
        );
      })
      .map((load: Object3D | Load) => load.id)
      .filter((loadId: number): boolean => loadId !== checkedObject.id)
      .filter((loadId: number): boolean => (objectId === null ? true : loadId !== objectId));
    const front: Object3D = this.mainView.loadingSpace.visualisationParts.front;
    const frontSide: Box3 = front
      ? new Box3().setFromObject(this.mainView.loadingSpace.visualisationParts.front)
      : new Box3().setFromObject(this.mainView.renderingContext.scene.getObjectByName('frontSide'));
    const leftSide: Box3 = new Box3().setFromObject(
      this.mainView.renderingContext.scene.getObjectByName('leftSide'),
    );
    const rightSide: Box3 = new Box3().setFromObject(
      this.mainView.renderingContext.scene.getObjectByName('rightSide'),
    );
    const backSide: Box3 = new Box3().setFromObject(
      this.mainView.renderingContext.scene.getObjectByName('backSide'),
    );
    const ceiling: Box3 = new Box3().setFromObject(
      this.mainView.renderingContext.scene.getObjectByName('Ceiling'),
    );
    const boundingBoxCollection: Box3[] = loadsIds.map((loadId: number) => {
      return new Box3().setFromObject(this.mainView.renderingContext.scene.getObjectById(loadId));
    });
    if (isAllowedOutOfSpace) {
      this.boundingBoxCollection = [
        ...boundingBoxCollection,
        leftSide,
        rightSide,
        frontSide,
        ceiling,
      ];
    } else {
      this.boundingBoxCollection = [
        ...boundingBoxCollection,
        leftSide,
        rightSide,
        backSide,
        frontSide,
        ceiling,
      ];
    }
  }

  private addBoundingBoxCollectionHelperBoxes(): void {
    this.boundingBoxCollection.forEach((box: Box3): void => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const helperBox2: Box3Helper = new Box3Helper(box, 0x00ff00);
      helperBox2.name = 'helperBox';
      this.mainView.renderingContext.scene.add(helperBox2);
    });
  }

  private detectCollision(object: Object3D, object2: Box3): boolean {
    const objectGeometry = !object.name.includes('container')
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
      ? object.geometry
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      : object.children[0].children[0].geometry;
    const geometry = objectGeometry.toNonIndexed();
    if (!geometry) {
      return false;
    }
    const objectMatrixWorld: Matrix4 = !object.name.includes('container')
      ? object.matrixWorld
      : object.children[0].children[0].matrixWorld;
    geometry.applyMatrix4(objectMatrixWorld);
    const position = geometry.getAttribute('position');
    const points: YUKA.Vector3 = [];
    for (let i = 0; i < position.count; i++) {
      const x = position.getX(i);
      const y = position.getY(i);
      const z = position.getZ(i);
      points.push(new YUKA.Vector3(x, y, z));
    }
    const obb = new YUKA.OBB().fromPoints(points);
    const isInCollision: boolean = obb.intersectsAABB(object2);
    return isInCollision;
  }

  public isInCollision(
    object: Object3D,
    isHelperBox = false,
    objectId: number = null,
    isOBBcheck = false,
  ): boolean {
    if (objectId) {
      this.prepareBoundingBoxes(object, objectId);
    } else {
      this.prepareBoundingBoxes(object);
    }
    const box3: Box3 = new Box3().setFromObject(object);
    // eslint-disable-next-line
    // @ts-ignore
    const orientedBoundingBox: OBB = new OBB().fromBox3(box3);
    const isIntersect: boolean = this.boundingBoxCollection.some((boundingBox: Box3): boolean => {
      return isOBBcheck
        ? this.detectCollision(object, boundingBox)
        : orientedBoundingBox.intersectsBox3(boundingBox);
    });
    if (isHelperBox) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const helperBox: Box3Helper = new Box3Helper(box3, 0x0000ff);
      helperBox.name = 'helperBox';
      this.mainView.renderingContext.scene.add(helperBox);
      this.addBoundingBoxCollectionHelperBoxes();
    }
    return isIntersect;
  }
}
